import React, { useContext, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import {
  Checkbox,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  useMediaQuery,
} from '@material-ui/core';
import moment from 'moment';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import { getZ3PConfigValue } from '../../helper';
import AvailabilityFields from './AvailabilityFields';
import { CalendarEditAvailabilityContext } from '../../Context';
import {
  convertToMinutes,
  createTimeBlocksFromExistingArray,
  generateTimes,
  recalculateDurations,
} from './availibilityFieldHelpers';

const useStyles = makeStyles(() => ({
  rowGrid: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: 'auto',
    marginTop: '20px !important',
  },
  timeGrid: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  listLabel: {
    color: '#888',
    marginRight: '15px',
  },
  expandedSection: {
    padding: '10px 0 10px 20px',
    marginTop: '10px',
  },
  timeInput: {
    '& .MuiOutlinedInput-adornedEnd': {
      padding: '0',
    },
    '& .MuiIconButton-root': {
      display: 'none',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 6px',
    },
    '& input[type="time"]::-webkit-calendar-picker-indicator': {
      display: 'none',
      '-webkit-appearance': 'none',
    },
  },
  durationInput: {
    '& .MuiOutlinedInput-input': {
      padding: '10px',
      paddingLeft: '0 !important',
    },
    '& .MuiSelect-iconOutlined': {
      right: 0,
    },
  },
  unavailableText: {
    color: '#949393',
  },
  tableCell: {
    border: 'none',
    paddingTop: 0,
    paddingBottom: '5px',
  },
  paddingRight10: {
    paddingRight: '10px !important',
    paddingLeft: 0,
  },
  tableCellLeft: {
    paddingLeft: '10px',
    paddingRight: '0 !important',
    textAlign: 'right',
  },
  paddingTop5: {
    paddingTop: '5px !important',
  },
}));

const RecurringScheduleRow = ({ dayDetails, isServiceBay, rowIndex }) => {
  const {
    recurringDayDetails,
    setRecurringDayDetails,
    allScheduleTypes,
    allClientLocations,
    hasWorkplaceService,
    recurringDayErrors,
    setRecurringDayErrors,
    selectedRecurringDays,
    setSelectedRecurringDays,
    copiedTimeBlocks,
    setCopiedTimeBlocks,
  } = useContext(CalendarEditAvailabilityContext);
  const {
    day_of_week: dayOfWeek,
    is_canceled: isCanceled,
    start_time: startTime,
    end_time: endTime,
    limit_scheduled_works: limitScheduledWorks,
    limit_total_driving_hours: limitTotalDrivingHours,
    offered_service_types: offeredServiceTypes,
    client_location_ids: clientLocationIds,
    schedule_dates_with_active_sws: scheduleDatesWithActiveSws,
    toggles: { isExpanded },
    predetermined_slot_times: predeterminedSlotTimes,
  } = dayDetails;

  const defaultDuration = 1;
  const { is_using_time_blocks: isUsingTimeBlocks } = getZ3PConfigValue();
  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const [shouldShowSwWarning, setShouldShowSwWarning] = useState(false);
  const [recurringTimeBlocks, setRecurringTimeBlocks] = useState([]);
  const [fieldError, setFieldError] = useState(false);
  const [startTimeError, setStartTimeError] = useState(false);
  const [endTimeError, setEndTimeError] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (isUsingTimeBlocks) {
      if (copiedTimeBlocks.length > 0 && !isCanceled) {
        setRecurringTimeBlocks(copiedTimeBlocks);

        // Clear copied time blocks once they've been set
        setCopiedTimeBlocks([]);
      } else {
        const slotTimes =
          predeterminedSlotTimes.length > 0
            ? createTimeBlocksFromExistingArray(
                predeterminedSlotTimes,
                startTime,
                endTime,
              )
            : generateTimes(startTime, endTime, defaultDuration);
        setRecurringTimeBlocks(slotTimes);
      }
    }
  }, [copiedTimeBlocks, predeterminedSlotTimes]);

  const updateDayErrors = (hasError) => {
    const updatedDayErrors = [...recurringDayErrors];
    updatedDayErrors[rowIndex] = hasError;
    setRecurringDayErrors(updatedDayErrors);
  };

  const clearErrors = () => {
    setStartTimeError(false);
    setEndTimeError(false);
    setFieldError(false);
    updateDayErrors(false);
  };

  const handleRecurringEventChange = (key, value, isToggle) => {
    const newRecurringArray = recurringDayDetails.map((object) => {
      if (object.day_of_week === dayOfWeek) {
        if (isToggle) {
          return {
            ...object,
            [key]: value,
            toggles: {
              ...object.toggles,
              [key]: value,
            },
          };
        }
        if (['start_time', 'end_time'].includes(key)) {
          const formattedTime = `${value}:00`;

          if (isUsingTimeBlocks) {
            const timeMinutes = convertToMinutes(formattedTime);
            const startTimeMinutes = convertToMinutes(startTime);
            const endTimeMinutes = convertToMinutes(endTime);
            let isInvalid = false;
            if (key.includes('start_time')) {
              isInvalid = timeMinutes > endTimeMinutes;
              if (!isInvalid) {
                const updatedTimes = recurringTimeBlocks.map(
                  ({ time, duration }) => {
                    const currentTimeMinutes = convertToMinutes(time);
                    return {
                      time,
                      duration,
                      error:
                        currentTimeMinutes < timeMinutes ||
                        currentTimeMinutes > endTimeMinutes,
                    };
                  },
                );
                const recalculatedTimes = recalculateDurations(
                  updatedTimes,
                  formattedTime,
                  endTime,
                );
                setRecurringTimeBlocks(recalculatedTimes);
                clearErrors();
                updateDayErrors(recalculatedTimes.some((time) => time.error));
              } else {
                setFieldError(isInvalid);
                setStartTimeError(isInvalid);
                updateDayErrors(isInvalid);
              }
            } else if (key.includes('end_time')) {
              isInvalid = timeMinutes < startTimeMinutes;

              if (!isInvalid) {
                const updatedTimes = recurringTimeBlocks.map(
                  ({ time, duration }) => {
                    const currentTimeMinutes = convertToMinutes(time);
                    return {
                      time,
                      duration,
                      error:
                        currentTimeMinutes >= timeMinutes ||
                        currentTimeMinutes < startTimeMinutes,
                    };
                  },
                );
                const recalculatedTimes = recalculateDurations(
                  updatedTimes,
                  startTime,
                  formattedTime,
                );
                setRecurringTimeBlocks(recalculatedTimes);
                clearErrors();
                updateDayErrors(recalculatedTimes.some((time) => time.error));
              } else {
                setFieldError(isInvalid);
                setEndTimeError(isInvalid);
                updateDayErrors(isInvalid);
              }
            }
          }

          return {
            ...object,
            [key]: formattedTime,
            is_changed: true,
            is_canceled: false,
            toggles: {
              ...object.toggles,
              isExpanded:
                offeredServiceTypes.length === 0 ||
                recurringTimeBlocks.some((time) => time.error)
                  ? true
                  : isExpanded,
            },
          };
        }
        if (key === 'is_canceled' && !!value) {
          return {
            ...object,
            [key]: value,
            is_changed: true,
            error: false,
            toggles: {
              ...object.toggles,
              isExpanded: false,
            },
          };
        }
        return {
          ...object,
          [key]: value,
          predetermined_slot_times: recurringTimeBlocks.map(
            (time) => time.time,
          ),
          is_changed: true,
          is_canceled: key === 'is_canceled' ? value : false,
          toggles: {
            ...object.toggles,
            isExpanded: offeredServiceTypes.length === 0 ? true : isExpanded,
          },
        };
      }
      return object;
    });
    setRecurringDayDetails(newRecurringArray);
  };

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // If a user is canceling a recurrence that has active SWs, show a warning first
  const handleSwitchChange = () => {
    if (!isCanceled && scheduleDatesWithActiveSws?.length > 0) {
      setShouldShowSwWarning(true);
    } else {
      handleRecurringEventChange('is_canceled', !isCanceled);
    }
  };

  const renderRulesTable = () => (
    <Table className={classes.expandedSection} size="small">
      <TableBody>
        <TableRow>
          <TableCell
            component="th"
            scope="row"
            className={`${classes.tableCell} ${classes.paddingTop5} ${classes.tableCellLeft}`}
          >
            <Tooltip
              title="Limit the total number of appointments for the day. Once the appointment limit is reached, customers looking to schedule will not see any times available. Setting the appointment limit to 0 will block off the day for customers but allow you to manually schedule if needed."
              alt="Limit the total number of appointments for the day. Once the appointment limit is reached, customers looking to schedule will not see any times available. Setting the appointment limit to 0 will block off the day for customers but allow you to manually schedule if needed."
            >
              <Typography gutterBottom className={classes.listLabel}>
                Appointment Limit:
              </Typography>
            </Tooltip>
          </TableCell>
          <TableCell
            className={`${classes.tableCell} ${classes.paddingTop5} ${classes.paddingRight10}`}
          >
            <TextField
              id="appointment-limit"
              type="number"
              variant="standard"
              value={limitScheduledWorks}
              onChange={(e) => {
                handleRecurringEventChange(
                  'limit_scheduled_works',
                  e.target.value,
                );
              }}
            />
          </TableCell>
        </TableRow>
        {!isServiceBay && (
          <TableRow>
            <TableCell
              className={`${classes.tableCell} ${classes.tableCellLeft}`}
            >
              <Tooltip
                title="Limit the cumulative time spent driving for the day. Customers looking to schedule will see no available times if their drive time pushes the cumulative drive time over this limit."
                alt="Limit the cumulative time spent driving for the day. Customers looking to schedule will see no available times if their drive time pushes the cumulative drive time over this limit."
              >
                <Typography gutterBottom className={classes.listLabel}>
                  Driving limit:
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell
              className={`${classes.tableCell} ${classes.paddingRight10}`}
            >
              <TextField
                key={`${dayOfWeek}-drive-time-limit`}
                type="number"
                value={limitTotalDrivingHours || null}
                onChange={(e) => {
                  handleRecurringEventChange(
                    'limit_total_driving_hours',
                    e.target.value,
                  );
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">hr</InputAdornment>
                  ),
                }}
              />
            </TableCell>
          </TableRow>
        )}
        {allScheduleTypes.length > 1 && (
          <TableRow>
            <TableCell
              className={`${classes.tableCell} ${classes.tableCellLeft}`}
            >
              <Tooltip
                title="Select service types to be performed on this day."
                alt="Select service types to be performed on this day."
              >
                <Typography className={classes.listLabel}>
                  Service Types:
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell
              className={`${classes.tableCell} ${classes.paddingRight10}`}
            >
              <Select
                className="schedule-type-select"
                size="large"
                multiple
                value={offeredServiceTypes}
                error={offeredServiceTypes.length === 0}
                onChange={(event) => {
                  handleRecurringEventChange(
                    'offered_service_types',
                    event.target.value,
                    false,
                  );
                }}
              >
                {allScheduleTypes.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>
        )}
        {hasWorkplaceService && allClientLocations.length > 0 && (
          <TableRow>
            <TableCell
              className={`${classes.tableCell} ${classes.tableCellLeft}`}
            >
              <Typography className={classes.listLabel}>Locations:</Typography>
            </TableCell>
            <TableCell
              className={`${classes.tableCell} ${classes.paddingRight10}`}
            >
              <Select
                className="client-location-select"
                size="large"
                multiple
                value={clientLocationIds}
                onChange={(event) => {
                  handleRecurringEventChange(
                    'client_location_ids',
                    event.target.value,
                    false,
                  );
                }}
              >
                {allClientLocations.map(({ id, short_name }) => (
                  <MenuItem key={id} value={id}>
                    {short_name}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Dialog
        open={shouldShowSwWarning}
        onClose={() => setShouldShowSwWarning(false)}
      >
        <DialogContent>
          <DialogContentText>
            The following date(s) have active booked appointments:{' '}
            {scheduleDatesWithActiveSws?.join(', ')}.
          </DialogContentText>
          <DialogContentText>
            This change will prevent future customers from booking this date,
            but the active bookings will remain.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShouldShowSwWarning(false);
            }}
            color="secondary"
          >
            Go Back
          </Button>
          <Button
            onClick={() => {
              handleRecurringEventChange('is_canceled', !isCanceled);
              setShouldShowSwWarning(false);
            }}
            color="primary"
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Grid
        className={classes.rowGrid}
        id={`${dayOfWeek}-row`}
        key={`${dayOfWeek}-row`}
        container
        direction="column"
        justify="space-between"
        xs={12}
        md={8}
        lg={10}
        xl={8}
      >
        <Grid item xs={1}>
          {isUsingTimeBlocks ? (
            <Checkbox
              color="primary"
              key={`${dayOfWeek}-switch`}
              checked={!isCanceled}
              onChange={(e) => {
                handleSwitchChange();
                const dayOfWeekIndex = daysOfWeek.indexOf(dayOfWeek);
                const selectedRecurringDaysCopy = [...selectedRecurringDays];
                selectedRecurringDaysCopy[dayOfWeekIndex] = e.target.value;
                setSelectedRecurringDays(selectedRecurringDaysCopy);
              }}
            />
          ) : (
            <Switch
              color="primary"
              key={`${dayOfWeek}-switch`}
              checked={!isCanceled}
              onChange={() => {
                handleSwitchChange();
              }}
            />
          )}
        </Grid>
        <Grid item xs={1}>
          <Typography>
            <strong>{dayOfWeek.slice(0, 3)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Grid container xs={12} className={classes.timeGrid}>
            <Grid item xs={5} className={classes.timeInput}>
              {isCanceled ? (
                <Typography className={classes.unavailableText}>
                  Unavailable
                </Typography>
              ) : (
                <TextField
                  id={`${dayOfWeek}-start`}
                  key={`${dayOfWeek}-start`}
                  type="time"
                  variant={isSmall ? 'standard' : 'outlined'}
                  value={moment(dayDetails.start_time, 'HH:mm:ss').format(
                    'HH:mm',
                  )}
                  error={startTimeError}
                  onChange={(e) => {
                    handleRecurringEventChange('start_time', e.target.value);
                  }}
                />
              )}
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={5} className={classes.timeInput}>
              {isCanceled ? (
                <Typography className={classes.unavailableText}>
                  Unavailable
                </Typography>
              ) : (
                <TextField
                  id={`${dayOfWeek}-end`}
                  key={`${dayOfWeek}-end`}
                  type="time"
                  variant={isSmall ? 'standard' : 'outlined'}
                  value={moment(endTime, 'HH:mm:ss').format('HH:mm')}
                  error={endTimeError}
                  onChange={(e) => {
                    handleRecurringEventChange('end_time', e.target.value);
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={1}
          className={isExpanded ? classes.cursorPointer : ''}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {!isCanceled &&
            (isExpanded ? (
              <KeyboardArrowDownIcon
                className={classes.cursorPointer}
                onClick={() =>
                  handleRecurringEventChange('isExpanded', !isExpanded, true)
                }
              />
            ) : (
              <KeyboardArrowRightIcon
                className={classes.cursorPointer}
                onClick={() =>
                  handleRecurringEventChange('isExpanded', !isExpanded, true)
                }
              />
            ))}
          {!isCanceled &&
            (fieldError || recurringTimeBlocks.some((time) => time.error)) && (
              <Tooltip
                title="One of the time blocks has an error. Click here to expand details."
                aria-label="One of the time blocks has an error. Click here to expand details."
              >
                <WarningIcon
                  style={{ color: 'red', cursor: 'pointer' }}
                  onClick={() =>
                    handleRecurringEventChange('isExpanded', true, true)
                  }
                />
              </Tooltip>
            )}
        </Grid>
        {isExpanded &&
          (isUsingTimeBlocks ? (
            <AvailabilityFields
              timeBlocks={recurringTimeBlocks}
              setTimeBlocks={setRecurringTimeBlocks}
              startTime={startTime}
              endTime={endTime}
              dayOfWeek={dayOfWeek}
              isServiceBay={isServiceBay}
              renderRulesTable={renderRulesTable}
            />
          ) : (
            renderRulesTable()
          ))}
      </Grid>
    </>
  );
};

export default RecurringScheduleRow;
